import React, { useContext, useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, Flex, FormControl, FormLabel, Image, Input, Link, Spinner, Text, VStack } from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { UserContext } from './UserContext';
import { authReturn, authDestination, rememberAuthReturn } from './client/authContinuation';
import { authPost, completeBrowserSession } from './auth/socialAuth';
import { providerName } from './auth/ProviderButtons';
import { signupAddress, signupDestination, signupLinkProof, clearSignupProof } from './auth/signupEmailFlow';
import './App.css';

// One setup page: email users verify and choose a password; provider users
// already have their account/session and only finish the remaining setup.
export default function Authorization() {
  const [email, setEmail] = useState(signupAddress);
  const [knownEmail] = useState(Boolean(signupAddress()));
  const [signupToken,setSignupToken] = useState(() => signupLinkProof(signupAddress()));
  const [linkVerified,setLinkVerified] = useState(false);
  const [linkReturn,setLinkReturn] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [bio, setBio] = useState('');
  const [terms, setTerms] = useState(false);
  const [profile, setProfile] = useState(null);
  const [providerEntry] = useState(Boolean(localStorage.getItem('token') && !signupAddress() && !signupToken));
  const [checking, setChecking] = useState(providerEntry || Boolean(signupToken));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const { setUserDetails } = useContext(UserContext);
  const navigate = useNavigate();
  const target = authReturn() || profile?.return_to || linkReturn || '/brief';
  useEffect(() => {
    if (!checking) return;
    let cancelled = false;
    (async () => {
      try {
        if (signupToken) {
          const response = await fetch('/api/auth/signup-link',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({email,signupToken})});
          const data = await response.json();
          if (cancelled) return;
          if (!response.ok) throw new Error(data.error || 'Could not check your setup link. Please try again.');
          setEmail(data.email);setFirstName(data.first_name || '');setLastName(data.last_name || '');setBio(data.bio || '');setTerms(data.terms_accepted);setLinkReturn(data.return_to);setLinkVerified(true);
          return;
        }
        const token = localStorage.getItem('token');
        const response = await fetch('/api/auth/onboarding', { headers: { Authorization: `Bearer ${token}` } });
        const data = await response.json();
        if (cancelled) return;
        if (response.status === 401) {
          localStorage.removeItem('token'); setUserDetails(null);
          navigate(authDestination('/login', authReturn()), { replace: true }); return;
        }
        if (!response.ok) throw new Error(data.error || 'Could not load your setup. Please try again.');
        if (!data.onboarding_pending) { await completeBrowserSession(token, authReturn() || data.return_to || '/brief', setUserDetails, navigate); return; }
        setProfile(data); setEmail(data.email); setFirstName(data.first_name || ''); setLastName(data.last_name || ''); setBio(data.bio || ''); setTerms(data.terms_accepted);
      } catch (failure) { if (!cancelled) setError(failure.message); }
      finally { if (!cancelled) setChecking(false); }
    })();
    return () => { cancelled = true; };
    // Loading is owned by this page entry, not unstable context callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const finish = async event => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      rememberAuthReturn(target);
      let result;
      if (profile) result = await authPost('onboarding', { firstName,lastName,bio, accepted_terms: terms, terms_version: profile.terms_version, return_to: target });
      else {
        const response = await fetch('/api/authorize', { method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ email, ...(linkVerified ? {signupToken} : {registrationCode:code}), password, firstName,lastName,bio, accepted_terms: terms }) });
        result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || 'Could not finish setup. Please try again.');
      }
      sessionStorage.removeItem('pabs-signup-email');
      clearSignupProof(email);
      await completeBrowserSession(result.token, (profile ? result.return_to : authReturn() || result.return_to) || target, setUserDetails, navigate);
    } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    setResending(true);
    try {
      const response = await fetch('/api/resend-signup-code', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, return_to: target }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not send another code.');
      clearSignupProof(email);
      navigate(signupDestination('/thank-you',email,target),{replace:true});
    } catch (failure) { setError(failure.message); }
    finally { setResending(false); }
  };
  const enterCode = () => { clearSignupProof(email);setSignupToken('');setLinkVerified(false);setError(''); };
  const signOut = () => {
    localStorage.removeItem('token'); sessionStorage.removeItem('pabs-signup-email'); setUserDetails(null);
    window.dispatchEvent(new Event('storage')); navigate(authDestination('/login', target), { replace: true });
  };
  return <Flex className="start-container" direction={{ base: 'column', md: 'row' }}>
    <Box flex="1.5" minW={0} pr={{ base: 0, md: '5%' }}>
      <Link as={RouterLink} to="/"><Image src="/images/pablo_logo1.0.svg" alt="Pabs home" boxSize="48px" mb="5%" /></Link>
      <Text as="h1" fontSize={{ base: '4xl', md: '6xl' }} fontWeight="bold" letterSpacing="tight" color="brand.900" lineHeight={1.1} overflowWrap="anywhere">
        {profile ? `Welcome to Pabs${profile.first_name ? `, ${profile.first_name}` : ''}` : checking ? 'Getting ready…' : providerEntry ? 'Welcome to Pabs' : 'Finish setting up'}
      </Text>
      {!checking && (!providerEntry || profile) && <Text mt={4} fontSize="lg" color="gray.700">{profile
        ? `You’re signed in${profile.provider ? ` with ${providerName(profile.provider)}` : ''}${email ? ` as ${email}` : ''}. Let’s get you started.`
        : linkVerified ? `Email confirmed${email ? ` for ${email}` : ''}. Choose a password and add your details.`
        : signupToken ? 'Open the latest setup link, or enter the code from your email.'
        : `Enter the code we emailed${email ? ` to ${email}` : ' you'} and choose a password.`}</Text>}
    </Box>
    <Box className="container-box" minW={0} flex={{ base: 'none', md: '1' }} mt={{ base: 6, md: '5%' }}>
      <VStack align="stretch" spacing={5} p="5%">
        {error && <Alert status="error" role="alert">{error}</Alert>}
        {checking ? <Box role="status"><Spinner size="sm" mr={3} />Loading your setup…</Box> : (providerEntry && !profile) || (signupToken && !linkVerified)
          ? <Button colorScheme="brand" onClick={() => window.location.reload()}>Try again</Button> : <form onSubmit={finish}>
          <VStack align="stretch" spacing={5}>
            {!profile && <>
              {!knownEmail && <FormControl isRequired><FormLabel>Email</FormLabel><Input placeholder="Your Email" _placeholder={{ color: 'gray.600' }} type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} bg="white" /></FormControl>}
              {!linkVerified && <FormControl isRequired><FormLabel>Email code</FormLabel><Input placeholder="6-digit code" _placeholder={{ color: 'gray.600' }} inputMode="numeric" autoComplete="one-time-code" value={code} onChange={e => setCode(e.target.value)} maxLength={6} bg="white" /></FormControl>}
              <FormControl isRequired><FormLabel>Choose a password</FormLabel><Input placeholder="Choose a password" _placeholder={{ color: 'gray.600' }} type="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} bg="white" /></FormControl>
            </>}
            <Flex gap={4} direction={{base:'column',md:'row'}}>
              <FormControl isRequired minW={0}><FormLabel>First name</FormLabel><Input autoComplete="given-name" value={firstName} onChange={e=>setFirstName(e.target.value)} maxLength={255} bg="white" /></FormControl>
              <FormControl minW={0}><FormLabel>Last name <Text as="span" fontWeight="normal">(optional)</Text></FormLabel><Input autoComplete="family-name" value={lastName} onChange={e=>setLastName(e.target.value)} maxLength={255} bg="white" /></FormControl>
            </Flex>
            <FormControl><FormLabel>Why would you like to use Pabs? <Text as="span" fontWeight="normal">(optional)</Text></FormLabel><Input value={bio} onChange={e => setBio(e.target.value)} maxLength={4000} bg="white" /></FormControl>
            {!profile?.terms_accepted && <Checkbox isChecked={terms} onChange={e => setTerms(e.target.checked)}>I agree to the <Link href="/terms" target="_blank" rel="noopener noreferrer" textDecoration="underline">Terms and Conditions</Link>.</Checkbox>}
            <Button colorScheme="brand" type="submit" width="100%" isDisabled={!terms} isLoading={busy} loadingText="Finishing setup…">Continue</Button>
          </VStack>
        </form>}
        {!checking && (providerEntry
          ? <Button variant="link" color="gray.700" textDecoration="underline" size="sm" alignSelf="flex-start" onClick={signOut}>Sign out</Button>
          : <Box>{signupToken && !linkVerified && <Button variant="link" color="gray.700" textDecoration="underline" size="sm" mb={3} onClick={enterCode}>Enter a code instead</Button>}
            <Text fontSize="sm" color="gray.700">Need another email? <Button variant="link" color="gray.700" textDecoration="underline" size="sm" isLoading={resending} onClick={resend}>Resend email</Button></Text>
            <Link as={RouterLink} to={authDestination('/register', target)} mt={3} display="inline-block" fontSize="sm" textDecoration="underline">Use a different email</Link></Box>)}
      </VStack>
    </Box>
  </Flex>;
}
